(ns ui.state.named-values
  "Option lists for `su/option-field` fields, resolved on demand.

   These options are tenant-configured `:entity.type/named-value` rows, not a
   compiled-in enum, and the new grid does not bulk-sync that type - thousands
   per tenant, and it is exactly the select-box content the scoping was written
   for (see ai/sync-model.md). Reading them from `@session/entities` therefore
   yields nothing there, which shows up as an empty dropdown and, for a value
   already stored, an id that cannot be labelled.

   ## Old grid is the source of truth where it has one

   An account that gets the legacy bulk sync has all of these rows in
   `@session/entities`. The functions here use the session for such an account.
   The behavior of the old grid does not change. A new value is visible at
   once, which the search index cannot promise because it is eventually consistent.

   On the new grid the session does NOT have the whole list. It has only the
   rows that an open entity points at. The entity-scope subscription of that
   entity puts them there. A row in the session does not show that the other rows
   were loaded. The sync mode of the account decides this
   (`session/bulk-synced-entity-type?`). The presence of a row does not.
   The functions merge the session rows into the fetched rows by `:db/id`. The
   session row wins. A value that the user creates or picks is then visible at once.

   ## What is cached

   Whole rows, not `{:value :label}` pairs, because callers filter and sort on
   them: `frontend.utils/named-value-options` sorts by `:common/ord` and drops
   values archived in the current pipeline. Index rows are normalised into the
   session entity's shape so a caller cannot tell the two apart - with one
   exception it must not read directly: pipeline scoping, which the two sources
   express inversely. The session entity carries
   `:named-value/archived-in-pipelines`; the index carries `extras.pipeline`,
   the pipelines the value is ACTIVE in (see `active-in-pipeline-ids` in
   backend.search.entity-indices). Ask `active-in-pipeline?` rather than
   reading either.

   ## Laziness

   The cache is a ratom, so a component that reads it re-renders when the fetch
   lands. Reading also starts the fetch, guarded so it happens at most once per
   attribute - see `rows` for why that side effect is deliberate. Priming on
   mount with `ensure-loaded!` (or the `::ensure-loaded` event) is still worth
   doing where a view has an obvious place for it, because it starts before
   first paint. Either way a caller must tolerate an empty list on the first
   render rather than treating it as \"no options exist\"."
  (:require
   [common.entity :as entity]
   [frontend.conn :as conn]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [promesa.core :as p]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [reagent.ratom :as ratom]))

(defonce ^:private cache
  ;; attribute -> vector of normalised rows. A ratom rather than app-db so that
  ;; plain helper fns (frontend.utils/named-value-options and its callers) stay
  ;; reactive without every one of them becoming a subscription.
  (r/atom {}))

(defonce ^:private in-flight
  (atom #{}))

(def ^:private active-pipelines-key
  "Normalised index rows carry their active pipelines here. Deliberately a
   namespaced key that no Datomic entity has, so `active-in-pipeline?` can tell
   an index row from a session entity."
  ::active-pipelines)

(defn- hit->row
  "One index hit as a session-entity-shaped map. `extras.pipeline` is kept under
   a private key rather than inverted back into
   `:named-value/archived-in-pipelines`, which cannot be reconstructed without
   knowing every pipeline the value could apply to."
  [{:keys [_source] :as _hit}]
  (let [{:keys [original extras]} _source]
    (cond-> {:db/id                 (:db/id original)
             :entity/type           :entity.type/named-value
             :named-value/attribute (:named-value/attribute original)
             :named-value/value     (:named-value/value original)
             :common/ord            (:common/ord original)
             :common/archived?      (boolean (:archived extras))
             active-pipelines-key   (set (:pipeline extras))}
      ;; Some index documents do not have it. Its shape also differs from the
      ;; shape in the session entity (`[{:db/id ..}]` and not bare ids). Convert it.
      (seq (:named-value/archived-in-pipelines original))
      (assoc :named-value/archived-in-pipelines
             (set (map (fn [pipeline] (or (:db/id pipeline) pipeline))
                       (:named-value/archived-in-pipelines original)))))))

(defn active-in-pipeline?
  "Whether `named-value` is usable in `pipeline-id`.

   Handles both shapes, because the two sources express this inversely. A nil
   `pipeline-id` means 'not scoped to a pipeline' and never filters anything
   out, matching what the session-only code did before."
  [named-value pipeline-id]
  (cond
    (nil? pipeline-id)
    true

    (contains? named-value active-pipelines-key)
    (contains? (get named-value active-pipelines-key) pipeline-id)

    :else
    (not (some #{pipeline-id} (:named-value/archived-in-pipelines named-value)))))

(def ^:private page-size
  "The universal search endpoint returns this number of hits at most for each request."
  200)

(def ^:private max-pages
  "Stops an endless loop if the endpoint ignores `:from-index`."
  50)

(defn- fetch-all-rows!
  "A promise of all rows for `attribute`, in `:common/ord` order. The endpoint
   returns `page-size` hits at most for each response. This function requests
   pages until a page is short. Without the loop, an attribute with more values
   than `page-size` would lose the other values with no error. The endpoint sorts
   by name, so the lost values would not even be the last values by `:common/ord`."
  [attribute]
  (letfn [(page! [page-number hits]
            (-> (conn/sendp! :search/universal-search
                             {:query        ""
                              :entity-types [:entity.type/named-value]
                              :from-index   (* page-number page-size)
                              :filter-terms {"original.named-value/attribute.keyword"
                                             (subs (str attribute) 1)}
                              :sort-terms   [{"original.common/ord" {:order         "asc"
                                                                     :unmapped_type "double"}}]})
                (p/then (fn [{:keys [body]}]
                          ;; `:responses` is a seq. Use `first` and not an index,
                          ;; because `get-in` cannot index a list.
                          (let [page-hits (-> body :responses first :hits :hits)
                                all-hits  (into hits page-hits)]
                            (if (and (= (count page-hits) page-size)
                                     (< (inc page-number) max-pages))
                              (page! (inc page-number) all-hits)
                              (mapv hit->row all-hits)))))))]
    (page! 0 [])))

(defn- fetch!
  [attribute]
  ;; Not `ui.editors.open-search.view/open-search!`: that maps each hit down to
  ;; {:value :label} for a picker and discards the ord, archived flag and
  ;; pipeline scoping this cache exists to keep.
  (swap! in-flight conj attribute)
  (-> (fetch-all-rows! attribute)
      (p/then (fn [rows]
                (swap! cache assoc attribute rows)
                (swap! in-flight disj attribute)))
      (p/catch (fn [err]
                 (logging/error "named-value option fetch failed for "
                                attribute " >> " err)
                 ;; Leave the attribute uncached so a later mount can retry.
                 (swap! in-flight disj attribute)))))

(defn- bulk-synced?
  []
  (session/bulk-synced-entity-type? :entity.type/named-value))

(defn session-rows
  "The rows that the legacy bulk sync delivered. It returns nil for an account
   that does not get them. On the new grid the session has only the rows that an
   open entity points at, and never the whole list. This function does not read
   the session there."
  [attribute]
  (when (bulk-synced?)
    (seq (session/named-values attribute))))

(defn ensure-loaded!
  "Fetch this attribute's rows unless the session already has them, they are
   cached, or a fetch is already running. Safe to call on every mount."
  [attribute]
  (when (and attribute
             (not (bulk-synced?))
             (not (contains? @cache attribute))
             (not (contains? @in-flight attribute)))
    (fetch! attribute)))

(defn- merge-by-id
  "`fetched` with each session row on top of it, matched by `:db/id`. A session
   row is a real entity, so it wins over the index copy. The function adds a
   session row that was never fetched (a row that was just created, for example)."
  [fetched session-rows]
  (let [session-by-id (into {} (map (juxt :db/id identity)) session-rows)
        fetched-ids   (into #{} (map :db/id) fetched)]
    (into (mapv (fn [row] (get session-by-id (:db/id row) row)) fetched)
          (remove (fn [row] (contains? fetched-ids (:db/id row))))
          session-rows)))

(defn rows
  "Every known row for `attribute`. Reactive: reading this in a component
   re-renders it when a fetch lands.

   Reading also kicks off the fetch if nothing has yet. That is a side effect
   from a render path, which is normally worth avoiding - it is done here
   because `frontend.utils/named-value-options` has half a dozen callers and a
   picker whose owner forgot to prime it fails silently, as an empty dropdown
   with no error. `ensure-loaded!` is guarded on the sync mode, the cache and an
   in-flight set, so this runs at most once per attribute and cannot loop: the
   only ratom it writes is `cache`, and that happens when the response lands,
   not during the read. Priming explicitly on mount is still better where
   there is an obvious place for it, because it starts before first paint."
  [attribute]
  (ensure-loaded! attribute)
  (if (bulk-synced?)
    (or (session-rows attribute) [])
    (merge-by-id (get @cache attribute)
                 (session/named-values attribute))))

(defn row-by-id
  "The row for `id` under `attribute`, or nil while it is not known yet."
  [attribute id]
  (when id
    (some (fn [row] (when (= (:db/id row) id) row))
          (rows attribute))))

(defn remember-row!
  "Adds a row the user just created, in the session entity's shape, so every
   reader sees it before the search index has caught up. A no-op on an account
   whose session already holds every row."
  [row]
  (when (and (not (bulk-synced?))
             (:db/id row)
             (:named-value/attribute row))
    (swap! cache update (:named-value/attribute row)
           (fn [cached]
             (conj (vec (remove (fn [existing] (= (:db/id existing) (:db/id row)))
                                cached))
                   row)))))

(defn invalidate!
  "Forgets what was fetched for `attribute`, so the next read fetches it again.
   Call it after rows were created, renamed, reordered or removed by a path
   that does not go through `remember-row!` - a Manage Options save, say."
  [attribute]
  (swap! cache dissoc attribute))

(defn load-all!
  "A promise of the current rows for `attribute`, fetched fresh and also stored
   in the cache. For an editor that rewrites the list and so must not start from
   a stale copy. Resolves from the session on an account that is bulk-synced."
  [attribute]
  (if (bulk-synced?)
    (p/resolved (vec (session/named-values attribute)))
    (-> (fetch-all-rows! attribute)
        (p/then (fn [fetched]
                  (swap! cache assoc attribute fetched)
                  fetched)))))

(defn options
  "`rows` as `{:value :label}` pairs, ready for a select."
  [attribute]
  (->> (rows attribute)
       (remove :common/disabled?)
       (remove :common/archived?)
       (sort-by #(or (:common/ord %) 0))
       (mapv (fn [nv]
               {:value (:db/id nv)
                :label (entity/display-string nv)}))))

(defn usable-rows
  "Rows a user may actually pick for `attribute` in `pipeline-id`: not disabled,
   not archived, and active in that pipeline. The equivalent of
   `frontend.state.current-session/non-archived-named-values`, but answering for
   an index-sourced row too."
  [attribute pipeline-id]
  (->> (rows attribute)
       (remove :common/disabled?)
       (remove :common/archived?)
       (filter #(active-in-pipeline? % pipeline-id))))

(defn preferred-id
  "The named-value id to prefill a new entity with: the account's `preference`
   when it is still a usable option here, otherwise the first option by
   `:common/ord`.

   Shared so the entity creator and the entity modal cannot drift apart - they
   each carried their own copy of this rule."
  [attribute pipeline-id preference]
  (let [usable (usable-rows attribute pipeline-id)]
    (or (when (and preference
                   (some (fn [nv] (= (:db/id nv) preference)) usable))
          preference)
        (->> usable
             (sort-by #(or (:common/ord %) 0))
             first
             :db/id))))

(def create-time-prefill-attributes
  "Attributes whose options are read SYNCHRONOUSLY, to default a field at the
   instant a new entity is created rather than to fill a dropdown the user can
   watch load. A prefill has no second chance: if the rows have not arrived the
   field is simply left blank and nothing re-runs. So these few are warmed once
   the session's data is in, instead of lazily on first read."
  [:oppo/lead-type-v2
   :oppo/lead-source-v2])

(defn warm-create-time-prefills!
  "Called once the client has its session data. A no-op on an account that is
   bulk-synced these rows already."
  []
  (run! ensure-loaded! create-time-prefill-attributes))

(rf/reg-event-fx
 ::ensure-loaded
 [rf/trim-v]
 (fn [_ [attribute]]
   {::ensure-loaded-fx {:attribute attribute}}))

(rf/reg-fx
 ::ensure-loaded-fx
 (fn [{:keys [attribute]}]
   (ensure-loaded! attribute)))

(rf/reg-sub-raw
 ::options
 (fn [_ [_ attribute]]
   (ratom/reaction (options attribute))))

(rf/reg-sub-raw
 ::rows
 (fn [_ [_ attribute]]
   (ratom/reaction (rows attribute))))
